'use client';

import { c as _c } from "react/compiler-runtime";
import { useLocalPreferenceSelector } from '@shared/utils/context/LocalPreferenceContext';
import { useLocalPreferenceActions } from '@shared/utils/hooks/useLocalPreference';
import classNames from 'classnames';
import { Maximize2, Minimize2, Pause, Play, RotateCcw, Volume2, VolumeX } from 'lucide-react';
import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
type VideoPlayerProps = {
  src: string;
  poster?: string;
  className?: string;
  loop?: boolean;
  controls?: boolean;
  playsInline?: boolean;
  onPlay?: () => void;
  onPause?: () => void;
  onMute?: (muted: boolean) => void;
  preload?: 'auto' | 'metadata' | 'none';
  style?: React.CSSProperties;
  muted?: boolean;
  onMouseOver?: (e: React.MouseEvent<HTMLVideoElement>) => void;
  onClick?: (e: React.MouseEvent<HTMLVideoElement>) => void;
  autoPlay?: boolean;
  videoSize?: {
    width: number;
    height: number;
  };
  // Fill the parent box (parent controls size) instead of the intrinsic
  // aspect-video box — used by ViewPostMedia's fixed stage.
  fill?: boolean;
  bufferingIndicator?: React.ReactNode;
  deferLoadUntilClick?: boolean;
};

// 👇 forwardRef so parent can control the <video />
const VideoPlayer = forwardRef<HTMLVideoElement, VideoPlayerProps>((t0, ref) => {
  const $ = _c(133);
  const {
    src,
    poster,
    loop: t1,
    controls: t2,
    playsInline: t3,
    onPlay,
    onPause,
    onMute,
    preload: t4,
    style,
    autoPlay: t5,
    muted: t6,
    onMouseOver,
    onClick,
    className,
    videoSize,
    fill: t7,
    bufferingIndicator,
    deferLoadUntilClick: t8
  } = t0;
  const loop = t1 === undefined ? false : t1;
  const controls = t2 === undefined ? true : t2;
  const playsInline = t3 === undefined ? true : t3;
  const preload = t4 === undefined ? "metadata" : t4;
  const autoPlay = t5 === undefined ? false : t5;
  const muted = t6 === undefined ? false : t6;
  const fill = t7 === undefined ? false : t7;
  const deferLoadUntilClick = t8 === undefined ? false : t8;
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [showControls, setShowControls] = useState(true);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isHydrated, setIsHydrated] = useState(false);
  const [isBuffering, setIsBuffering] = useState(false);
  const [hasStartedLoading, setHasStartedLoading] = useState(!deferLoadUntilClick || autoPlay);
  const [showTempIcon, setShowTempIcon] = useState(false);
  const [tempIconType, setTempIconType] = useState("play");
  const internalVideoRef = useRef(null);
  const videoContainerRef = useRef(null);
  const controlsTimeoutRef = useRef(null);
  const tempIconTimeoutRef = useRef(null);
  const {
    updateMuteVideo
  } = useLocalPreferenceActions();
  const muteVideo = useLocalPreferenceSelector(_temp);
  let t9;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = () => internalVideoRef.current;
    $[0] = t9;
  } else {
    t9 = $[0];
  }
  useImperativeHandle(ref, t9);
  let t10;
  let t11;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = () => setIsHydrated(true);
    t11 = [];
    $[1] = t10;
    $[2] = t11;
  } else {
    t10 = $[1];
    t11 = $[2];
  }
  useEffect(t10, t11);
  let t12;
  if ($[3] !== autoPlay || $[4] !== deferLoadUntilClick) {
    t12 = () => {
      setHasStartedLoading(!deferLoadUntilClick || autoPlay);
      setIsBuffering(false);
      setIsPlaying(false);
      setCurrentTime(0);
      setDuration(0);
    };
    $[3] = autoPlay;
    $[4] = deferLoadUntilClick;
    $[5] = t12;
  } else {
    t12 = $[5];
  }
  let t13;
  if ($[6] !== autoPlay || $[7] !== deferLoadUntilClick || $[8] !== src) {
    t13 = [src, deferLoadUntilClick, autoPlay];
    $[6] = autoPlay;
    $[7] = deferLoadUntilClick;
    $[8] = src;
    $[9] = t13;
  } else {
    t13 = $[9];
  }
  useEffect(t12, t13);
  let t14;
  let t15;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = () => {
      const handleFullscreenChange = () => setIsFullscreen(Boolean(document.fullscreenElement));
      document.addEventListener("fullscreenchange", handleFullscreenChange);
      return () => document.removeEventListener("fullscreenchange", handleFullscreenChange);
    };
    t15 = [];
    $[10] = t14;
    $[11] = t15;
  } else {
    t14 = $[10];
    t15 = $[11];
  }
  useEffect(t14, t15);
  let t16;
  let t17;
  if ($[12] !== isHydrated) {
    t16 = () => {
      if (isHydrated && internalVideoRef.current) {
        setCurrentTime(0);
        setDuration(0);
        setIsPlaying(false);
        if (internalVideoRef.current.readyState >= 1) {
          setDuration(internalVideoRef.current.duration);
        }
      }
    };
    t17 = [isHydrated];
    $[12] = isHydrated;
    $[13] = t16;
    $[14] = t17;
  } else {
    t16 = $[13];
    t17 = $[14];
  }
  useEffect(t16, t17);
  let t18;
  let t19;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = () => {
      const fullscreenChange = () => {
        if (document.fullscreenElement) {
          return;
        }
        if (internalVideoRef.current) {
          internalVideoRef.current.controls = true;
          internalVideoRef.current.controls = false;
        }
      };
      document.addEventListener("fullscreenchange", fullscreenChange);
      return () => {
        if (tempIconTimeoutRef.current) {
          clearTimeout(tempIconTimeoutRef.current);
        }
        document.removeEventListener("fullscreenchange", fullscreenChange);
      };
    };
    t19 = [];
    $[15] = t18;
    $[16] = t19;
  } else {
    t18 = $[15];
    t19 = $[16];
  }
  useEffect(t18, t19);
  let t20;
  if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
    t20 = type => {
      if (tempIconTimeoutRef.current) {
        clearTimeout(tempIconTimeoutRef.current);
      }
      setTempIconType(type);
      setShowTempIcon(true);
      tempIconTimeoutRef.current = setTimeout(() => setShowTempIcon(false), 1000);
    };
    $[17] = t20;
  } else {
    t20 = $[17];
  }
  const showTemporaryIcon = t20;
  let t21;
  if ($[18] !== hasStartedLoading || $[19] !== isPlaying) {
    t21 = () => {
      if (!internalVideoRef.current) {
        return;
      }
      if (!hasStartedLoading) {
        setHasStartedLoading(true);
      }
      if (isPlaying) {
        internalVideoRef.current.pause();
        showTemporaryIcon("pause");
      } else {
        internalVideoRef.current.play();
        showTemporaryIcon("play");
      }
      setIsPlaying(!isPlaying);
    };
    $[18] = hasStartedLoading;
    $[19] = isPlaying;
    $[20] = t21;
  } else {
    t21 = $[20];
  }
  const handlePlayPause = t21;
  let t22;
  if ($[21] !== hasStartedLoading || $[22] !== isPlaying) {
    t22 = () => {
      if (!hasStartedLoading) {
        setHasStartedLoading(true);
        requestAnimationFrame(() => {
          internalVideoRef.current?.play();
        });
        return;
      }
      if (!internalVideoRef.current) {
        return;
      }
      if (isPlaying) {
        internalVideoRef.current.pause();
      } else {
        internalVideoRef.current.play();
      }
      setIsPlaying(!isPlaying);
    };
    $[21] = hasStartedLoading;
    $[22] = isPlaying;
    $[23] = t22;
  } else {
    t22 = $[23];
  }
  const handleControlPlayPause = t22;
  let t23;
  if ($[24] !== muteVideo || $[25] !== onMute || $[26] !== updateMuteVideo) {
    t23 = () => {
      if (!internalVideoRef.current) {
        return;
      }
      const newMutedState = !muteVideo;
      internalVideoRef.current.muted = newMutedState;
      updateMuteVideo(newMutedState);
      onMute?.(newMutedState);
    };
    $[24] = muteVideo;
    $[25] = onMute;
    $[26] = updateMuteVideo;
    $[27] = t23;
  } else {
    t23 = $[27];
  }
  const handleMuteToggle = t23;
  let t24;
  if ($[28] === Symbol.for("react.memo_cache_sentinel")) {
    t24 = () => {
      if (internalVideoRef.current) {
        setCurrentTime(internalVideoRef.current.currentTime);
      }
    };
    $[28] = t24;
  } else {
    t24 = $[28];
  }
  const handleTimeUpdate = t24;
  let t25;
  if ($[29] === Symbol.for("react.memo_cache_sentinel")) {
    t25 = () => {
      if (internalVideoRef.current) {
        setDuration(internalVideoRef.current.duration);
      }
    };
    $[29] = t25;
  } else {
    t25 = $[29];
  }
  const handleLoadedMetadata = t25;
  let t26;
  if ($[30] === Symbol.for("react.memo_cache_sentinel")) {
    t26 = e => {
      const time = parseFloat(e.target.value);
      if (internalVideoRef.current && !isNaN(time)) {
        internalVideoRef.current.currentTime = time;
        setCurrentTime(time);
      }
    };
    $[30] = t26;
  } else {
    t26 = $[30];
  }
  const handleSeek = t26;
  let t27;
  if ($[31] === Symbol.for("react.memo_cache_sentinel")) {
    t27 = () => {
      if (document.fullscreenElement) {
        document.exitFullscreen();
      } else {
        const webkitVideo = internalVideoRef.current as (HTMLVideoElement & {
          webkitEnterFullscreen?: () => void;
        }) | null;
        if (webkitVideo?.webkitEnterFullscreen) {
          webkitVideo.webkitEnterFullscreen();
        } else {
          videoContainerRef.current?.requestFullscreen();
        }
      }
    };
    $[31] = t27;
  } else {
    t27 = $[31];
  }
  const handleFullscreen = t27;
  let t28;
  if ($[32] === Symbol.for("react.memo_cache_sentinel")) {
    t28 = () => {
      if (internalVideoRef.current) {
        internalVideoRef.current.currentTime = 0;
        setCurrentTime(0);
      }
    };
    $[32] = t28;
  } else {
    t28 = $[32];
  }
  const handleRestart = t28;
  const formatTime = _temp2;
  let t29;
  if ($[33] !== isPlaying) {
    t29 = () => {
      setShowControls(true);
      if (controlsTimeoutRef.current) {
        clearTimeout(controlsTimeoutRef.current);
      }
      controlsTimeoutRef.current = setTimeout(() => {
        if (isPlaying) {
          setShowControls(false);
        }
      }, 3000);
    };
    $[33] = isPlaying;
    $[34] = t29;
  } else {
    t29 = $[34];
  }
  const handleMouseMove = t29;
  let t30;
  if ($[35] !== isPlaying) {
    t30 = e_0 => {
      const target = e_0.relatedTarget as HTMLElement | null;
      const isMovingToControls = target?.closest?.("[data-video-controls]");
      if (isPlaying && !isMovingToControls) {
        setShowControls(false);
      }
    };
    $[35] = isPlaying;
    $[36] = t30;
  } else {
    t30 = $[36];
  }
  const handleMouseLeave = t30;
  const t31 = isFullscreen ? "h-screen w-screen rounded-none" : fill ? "size-full rounded-lg" : videoSize ? "rounded-2xl" : "aspect-video rounded-2xl";
  let t32;
  if ($[37] !== className || $[38] !== t31) {
    t32 = classNames("relative overflow-hidden bg-black shadow-2xl", t31, className);
    $[37] = className;
    $[38] = t31;
    $[39] = t32;
  } else {
    t32 = $[39];
  }
  let t33;
  if ($[40] !== isFullscreen || $[41] !== videoSize) {
    t33 = !isFullscreen && videoSize ? {
      width: videoSize.width,
      height: videoSize.height,
      maxWidth: "100%"
    } : undefined;
    $[40] = isFullscreen;
    $[41] = videoSize;
    $[42] = t33;
  } else {
    t33 = $[42];
  }
  const t34 = isFullscreen || videoSize || fill ? "h-full w-full" : "aspect-video";
  let t35;
  if ($[43] !== t34) {
    t35 = classNames("relative", t34);
    $[43] = t34;
    $[44] = t35;
  } else {
    t35 = $[44];
  }
  const t36 = hasStartedLoading ? src : undefined;
  let t37;
  if ($[45] !== onPlay) {
    t37 = () => {
      setIsBuffering(false);
      setIsPlaying(true);
      onPlay?.();
    };
    $[45] = onPlay;
    $[46] = t37;
  } else {
    t37 = $[46];
  }
  let t38;
  if ($[47] !== onPause) {
    t38 = () => {
      setIsPlaying(false);
      onPause?.();
    };
    $[47] = onPause;
    $[48] = t38;
  } else {
    t38 = $[48];
  }
  let t39;
  let t40;
  let t41;
  if ($[49] === Symbol.for("react.memo_cache_sentinel")) {
    t39 = () => setIsBuffering(true);
    t40 = () => setIsBuffering(false);
    t41 = () => setIsBuffering(false);
    $[49] = t39;
    $[50] = t40;
    $[51] = t41;
  } else {
    t39 = $[49];
    t40 = $[50];
    t41 = $[51];
  }
  let t42;
  if ($[52] !== onMute || $[53] !== updateMuteVideo) {
    t42 = () => {
      if (internalVideoRef.current) {
        const isMuted = internalVideoRef.current.muted;
        updateMuteVideo(isMuted);
        onMute?.(isMuted);
      }
    };
    $[52] = onMute;
    $[53] = updateMuteVideo;
    $[54] = t42;
  } else {
    t42 = $[54];
  }
  let t43;
  if ($[55] !== hasStartedLoading || $[56] !== src) {
    t43 = hasStartedLoading && <source src={src} type="video/mp4" />;
    $[55] = hasStartedLoading;
    $[56] = src;
    $[57] = t43;
  } else {
    t43 = $[57];
  }
  let t44;
  if ($[58] !== autoPlay || $[59] !== loop || $[60] !== muted || $[61] !== onClick || $[62] !== onMouseOver || $[63] !== playsInline || $[64] !== poster || $[65] !== preload || $[66] !== style || $[67] !== t36 || $[68] !== t37 || $[69] !== t38 || $[70] !== t42 || $[71] !== t43) {
    t44 = <video ref={internalVideoRef} style={style} className="size-full object-contain" poster={poster} loop={loop} playsInline={playsInline} src={t36} preload={preload} autoPlay={autoPlay} muted={muted} onMouseOver={onMouseOver} onClick={onClick} onPlay={t37} onPause={t38} onWaiting={t39} onPlaying={t40} onCanPlay={t41} onVolumeChange={t42} onTimeUpdate={handleTimeUpdate} onLoadedMetadata={handleLoadedMetadata} controls={false}>{t43}Your browser does not support the video tag.</video>;
    $[58] = autoPlay;
    $[59] = loop;
    $[60] = muted;
    $[61] = onClick;
    $[62] = onMouseOver;
    $[63] = playsInline;
    $[64] = poster;
    $[65] = preload;
    $[66] = style;
    $[67] = t36;
    $[68] = t37;
    $[69] = t38;
    $[70] = t42;
    $[71] = t43;
    $[72] = t44;
  } else {
    t44 = $[72];
  }
  let t45;
  if ($[73] !== bufferingIndicator || $[74] !== deferLoadUntilClick || $[75] !== hasStartedLoading) {
    t45 = deferLoadUntilClick && !hasStartedLoading && <button type="button" className="absolute inset-0 z-10 flex items-center justify-center bg-black/30 text-white backdrop-blur-sm" onClick={e_1 => {
      e_1.stopPropagation();
      setHasStartedLoading(true);
      requestAnimationFrame(() => {
        internalVideoRef.current?.play();
      });
    }}>{bufferingIndicator ?? <div className="flex items-center gap-2 rounded-full bg-black/50 px-4 py-2 text-sm"><Play className="size-4" fill="white" /><span>Click to play</span></div>}</button>;
    $[73] = bufferingIndicator;
    $[74] = deferLoadUntilClick;
    $[75] = hasStartedLoading;
    $[76] = t45;
  } else {
    t45 = $[76];
  }
  let t46;
  if ($[77] !== bufferingIndicator || $[78] !== hasStartedLoading || $[79] !== isBuffering) {
    t46 = hasStartedLoading && isBuffering && <div className="pointer-events-none absolute inset-0 z-10 flex items-center justify-center">{bufferingIndicator ?? <div className="rounded-full bg-black/50 px-4 py-2 text-sm text-white">Buffering...</div>}</div>;
    $[77] = bufferingIndicator;
    $[78] = hasStartedLoading;
    $[79] = isBuffering;
    $[80] = t46;
  } else {
    t46 = $[80];
  }
  const t47 = `pointer-events-none absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent transition-opacity duration-300 ${showControls && controls ? "opacity-100" : "opacity-0"}`;
  let t48;
  if ($[81] !== showTempIcon || $[82] !== tempIconType) {
    t48 = showTempIcon && <div className="pointer-events-none absolute inset-0 flex items-center justify-center"><button className="anim-enter rounded-full bg-black/20 p-8 backdrop-blur-sm [--enter-scale:0]">{tempIconType === "play" ? <Play className="size-16 text-white" fill="white" /> : <Pause className="size-16 text-white" />}</button></div>;
    $[81] = showTempIcon;
    $[82] = tempIconType;
    $[83] = t48;
  } else {
    t48 = $[83];
  }
  let t49;
  if ($[84] !== currentTime || $[85] !== duration || $[86] !== isHydrated) {
    t49 = isHydrated && <input type="range" min="0" max={duration || 0} value={currentTime} onChange={handleSeek} step="0.1" className="h-2 w-full cursor-pointer appearance-none rounded-lg bg-white/20 transition-all hover:bg-white/30" style={{
      background: `linear-gradient(to right, white ${duration > 0 ? currentTime / duration * 100 : 0}%, rgba(255,255,255,0.2) ${duration > 0 ? currentTime / duration * 100 : 0}%)`
    }} />;
    $[84] = currentTime;
    $[85] = duration;
    $[86] = isHydrated;
    $[87] = t49;
  } else {
    t49 = $[87];
  }
  let t50;
  if ($[88] !== t49) {
    t50 = <div className="mb-4">{t49}</div>;
    $[88] = t49;
    $[89] = t50;
  } else {
    t50 = $[89];
  }
  let t51;
  if ($[90] !== isPlaying) {
    t51 = isPlaying ? <Pause className="size-6 text-white" /> : <Play className="size-6 text-white" fill="white" />;
    $[90] = isPlaying;
    $[91] = t51;
  } else {
    t51 = $[91];
  }
  let t52;
  if ($[92] !== handleControlPlayPause || $[93] !== t51) {
    t52 = <button onClick={handleControlPlayPause} className="rounded-full bg-white/20 p-2 backdrop-blur-sm transition-transform duration-200 hover:scale-[1.05] hover:bg-white/30 active:scale-[0.95]">{t51}</button>;
    $[92] = handleControlPlayPause;
    $[93] = t51;
    $[94] = t52;
  } else {
    t52 = $[94];
  }
  let t53;
  if ($[95] !== muteVideo) {
    t53 = muteVideo ? <VolumeX className="size-6 text-white" /> : <Volume2 className="size-6 text-white" />;
    $[95] = muteVideo;
    $[96] = t53;
  } else {
    t53 = $[96];
  }
  let t54;
  if ($[97] !== handleMuteToggle || $[98] !== t53) {
    t54 = <button onClick={handleMuteToggle} className="rounded-full bg-white/20 p-2 backdrop-blur-sm transition-transform duration-200 hover:scale-[1.05] hover:bg-white/30 active:scale-[0.95]">{t53}</button>;
    $[97] = handleMuteToggle;
    $[98] = t53;
    $[99] = t54;
  } else {
    t54 = $[99];
  }
  let t55;
  if ($[100] === Symbol.for("react.memo_cache_sentinel")) {
    t55 = <button onClick={handleRestart} className="rounded-full bg-white/20 p-2 backdrop-blur-sm transition-transform duration-200 hover:scale-[1.05] hover:bg-white/30 active:scale-[0.95]"><RotateCcw className="size-6 text-white" /></button>;
    $[100] = t55;
  } else {
    t55 = $[100];
  }
  const t56 = formatTime(currentTime);
  const t57 = formatTime(duration);
  let t58;
  if ($[101] !== t56 || $[102] !== t57) {
    t58 = <span className="text-sm text-white">{t56} / {t57}</span>;
    $[101] = t56;
    $[102] = t57;
    $[103] = t58;
  } else {
    t58 = $[103];
  }
  let t59;
  if ($[104] !== t52 || $[105] !== t54 || $[106] !== t58) {
    t59 = <div className="flex items-center space-x-4">{t52}{t54}{t55}{t58}</div>;
    $[104] = t52;
    $[105] = t54;
    $[106] = t58;
    $[107] = t59;
  } else {
    t59 = $[107];
  }
  let t60;
  if ($[108] !== isFullscreen) {
    t60 = <button onClick={handleFullscreen} className="rounded-full bg-white/20 p-2 backdrop-blur-sm transition-transform duration-200 hover:scale-[1.05] hover:bg-white/30 active:scale-[0.95]">{isFullscreen ? <Minimize2 className="size-6 text-white" /> : <Maximize2 className="size-6 text-white" />}</button>;
    $[108] = isFullscreen;
    $[109] = t60;
  } else {
    t60 = $[109];
  }
  let t61;
  if ($[110] !== t59 || $[111] !== t60) {
    t61 = <div className="flex items-center justify-between">{t59}{t60}</div>;
    $[110] = t59;
    $[111] = t60;
    $[112] = t61;
  } else {
    t61 = $[112];
  }
  let t62;
  if ($[113] !== t50 || $[114] !== t61) {
    t62 = <div className="pointer-events-auto absolute inset-x-0 bottom-0 p-6">{t50}{t61}</div>;
    $[113] = t50;
    $[114] = t61;
    $[115] = t62;
  } else {
    t62 = $[115];
  }
  let t63;
  if ($[116] !== t47 || $[117] !== t48 || $[118] !== t62) {
    t63 = <div data-video-controls={true} className={t47} onClick={_temp3}>{t48}{t62}</div>;
    $[116] = t47;
    $[117] = t48;
    $[118] = t62;
    $[119] = t63;
  } else {
    t63 = $[119];
  }
  let t64;
  if ($[120] !== handleMouseLeave || $[121] !== handleMouseMove || $[122] !== handlePlayPause || $[123] !== t35 || $[124] !== t44 || $[125] !== t45 || $[126] !== t46 || $[127] !== t63) {
    t64 = <div className={t35} onMouseMove={handleMouseMove} onMouseLeave={handleMouseLeave} onClick={handlePlayPause}>{t44}{t45}{t46}{t63}</div>;
    $[120] = handleMouseLeave;
    $[121] = handleMouseMove;
    $[122] = handlePlayPause;
    $[123] = t35;
    $[124] = t44;
    $[125] = t45;
    $[126] = t46;
    $[127] = t63;
    $[128] = t64;
  } else {
    t64 = $[128];
  }
  let t65;
  if ($[129] !== t32 || $[130] !== t33 || $[131] !== t64) {
    t65 = <div ref={videoContainerRef} className={t32} style={t33}>{t64}</div>;
    $[129] = t32;
    $[130] = t33;
    $[131] = t64;
    $[132] = t65;
  } else {
    t65 = $[132];
  }
  return t65;
});
VideoPlayer.displayName = 'VideoPlayer';
export default VideoPlayer;
function _temp(state) {
  return state.muteVideo;
}
function _temp2(time_0) {
  const m = Math.floor(time_0 / 60);
  const s = Math.floor(time_0 % 60);
  return `${m}:${s.toString().padStart(2, "0")}`;
}
function _temp3(e_2) {
  return e_2.stopPropagation();
}